/**
 * useFormStore
 *
 * Single consumer hook for the form store. Reads the Valtio snapshot, evaluates
 * form access, wraps each document / collection slot with set helpers, and
 * returns the consumer-facing shape.
 *
 * It deliberately does NOT call `useFormSplit`: that hook mounts a Firestore
 * `onSnapshot` per split table, so calling it here mounted one duplicate
 * listener set per mounted consumer. The two surfaces that need split options
 * (`FormSplitControl`, `WorkflowParameterContent`) hold `useFormSplit`
 * themselves. `splitQuery` is plain stored state on the proxy.
 *
 * The layout-editor preview drives the same singleton via
 * `formStore.loadPreview(...)`, so this hook has no preview branch.
 *
 * ⚠️ COARSE-GRAINED — DO NOT USE FROM LEAF/LIST CONSUMERS.
 *
 * The materialization useMemos below spread `snap.form.data`,
 * `snap.formVersion.data`, etc. Spreading reads the parent reference, so
 * Valtio's access tracking marks the entire `formVersion.data` object as
 * accessed. Any descendant write — including a single field-value or
 * cell-value change — invalidates that snapshot and re-renders every
 * consumer of this hook.
 *
 * For per-field, per-cell, or per-entity-reference consumers, use the
 * narrow scope hooks instead — they subscribe only to the specific leaf:
 *   - `useFieldUIScope(fieldId)` — for `useFieldUI`
 *   - `useEntityRefScope(referenceKey)` — for `useEntityRecordUI`
 *
 * The `no-restricted-syntax` rule in `react/eslint.config.js` enforces this: the
 * call errors everywhere except an explicit seven-file allowlist. Toolbars used
 * to be on the sanctioned list and are not any more — they mount alongside the
 * form body, which is exactly the condition that makes a coarse read expensive
 * (SYD-2252). What survives on the allowlist is unmounted while the user types:
 * modals, drawers, and the non-default sidebar views.
 */

import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type {
    Form,
    FormType,
    FormVersion,
    SplitQuery,
    UILayout,
} from '#types/System.types';
import { useEvaluateFormAccess } from '../../use/data/useEvaluateFormAccess';
import { formStore } from './formStore';
import { formActions } from './formActions';
import type {
    FormStoreDocument,
    FormStoreCollection,
    UseFormStoreReturn,
} from './useFormStore.types';

const materializeCollection = <T extends { docId?: string }>(
    docIds: readonly string[],
    docs: ReadonlyMap<string, { data: T }>,
): T[] => {
    const result: T[] = [];
    for (const id of docIds) {
        const entry = docs.get(id);
        if (entry) result.push({ ...entry.data, docId: id });
    }
    return result;
};

export const useFormStore = (): UseFormStoreReturn => {
    const snap = useSnapshot(formStore.state);

    const formVersionsData = useMemo(
        () => materializeCollection<FormVersion>(snap.formVersions.docIds, snap.formVersions.docs as ReadonlyMap<string, { data: FormVersion }>),
        [snap.formVersions.docIds, snap.formVersions.docs],
    );
    const formTypesData = useMemo(
        () => materializeCollection<FormType>(snap.formTypes.docIds, snap.formTypes.docs as ReadonlyMap<string, { data: FormType }>),
        [snap.formTypes.docIds, snap.formTypes.docs],
    );
    const uiLayoutsData = useMemo(
        () => materializeCollection<UILayout>(snap.uiLayouts.docIds, snap.uiLayouts.docs as ReadonlyMap<string, { data: UILayout }>),
        [snap.uiLayouts.docIds, snap.uiLayouts.docs],
    );

    const formVersionAccess = useEvaluateFormAccess({
        formId: snap.formId,
        versionId: snap.versionId,
        enabled: true,
    });

    const formData = useMemo<Form | null>(() => (
        snap.form.data ? { ...(snap.form.data as Form), docId: snap.formId } as Form : null
    ), [snap.form.data, snap.formId]);

    const formVersionData = useMemo<FormVersion | null>(() => (
        snap.formVersion.data ? { ...(snap.formVersion.data as FormVersion), docId: snap.versionId } as FormVersion : null
    ), [snap.formVersion.data, snap.versionId]);

    const formTypeData = useMemo<FormType | null>(() => {
        if (!snap.formType.data) return null;
        const formTypeId = (snap.form.data as Form | null)?.formType;
        return { ...(snap.formType.data as FormType), docId: formTypeId } as FormType;
    }, [snap.formType.data, snap.form.data]);

    const uiLayoutData = useMemo<UILayout | null>(() => (
        snap.uiLayout.data ? { ...(snap.uiLayout.data as UILayout), docId: snap.uiLayoutId } as UILayout : null
    ), [snap.uiLayout.data, snap.uiLayoutId]);

    const form: FormStoreDocument<Form> = useMemo(() => ({
        data: formData,
        loading: snap.form.loading,
        error: snap.form.error,
        set: async (updates: Partial<Form>) => {
            formStore.getFormPersist()?.setMerge(updates, { debounce: false });
        },
        debouncedSet: (updates: Partial<Form>) => {
            void formStore.getFormPersist()?.setMerge(updates, { debounce: true });
        },
    }), [formData, snap.form.loading, snap.form.error]);

    const formVersion: FormStoreDocument<FormVersion> = useMemo(() => ({
        data: formVersionData,
        loading: snap.formVersion.loading,
        error: snap.formVersion.error,
        set: async (updates: Partial<FormVersion>) => {
            formStore.getFormVersionPersist()?.setMerge(updates, { debounce: false });
        },
        debouncedSet: (updates: Partial<FormVersion>) => {
            void formStore.getFormVersionPersist()?.setMerge(updates, { debounce: true });
        },
    }), [formVersionData, snap.formVersion.loading, snap.formVersion.error]);

    const formType: FormStoreDocument<FormType> = useMemo(() => ({
        data: formTypeData,
        loading: snap.formType.loading,
        error: snap.formType.error,
        set: async (updates: Partial<FormType>) => {
            formStore.getFormTypePersist()?.setMerge(updates, { debounce: false });
        },
        debouncedSet: (updates: Partial<FormType>) => {
            void formStore.getFormTypePersist()?.setMerge(updates, { debounce: true });
        },
    }), [formTypeData, snap.formType.loading, snap.formType.error]);

    const uiLayout: FormStoreDocument<UILayout> = useMemo(() => ({
        data: uiLayoutData,
        loading: snap.uiLayout.loading,
        error: snap.uiLayout.error,
        set: async (updates: Partial<UILayout>) => {
            formStore.getUILayoutPersist()?.setMerge(updates, { debounce: false });
        },
        debouncedSet: (updates: Partial<UILayout>) => {
            void formStore.getUILayoutPersist()?.setMerge(updates, { debounce: true });
        },
    }), [uiLayoutData, snap.uiLayout.loading, snap.uiLayout.error]);

    const formVersions: FormStoreCollection<FormVersion> = useMemo(() => ({
        data: formVersionsData,
        loading: snap.formVersions.loading,
        error: snap.formVersions.error,
    }), [formVersionsData, snap.formVersions.loading, snap.formVersions.error]);

    const formTypes: FormStoreCollection<FormType> = useMemo(() => ({
        data: formTypesData,
        loading: snap.formTypes.loading,
        error: snap.formTypes.error,
    }), [formTypesData, snap.formTypes.loading, snap.formTypes.error]);

    const uiLayouts: FormStoreCollection<UILayout> = useMemo(() => ({
        data: uiLayoutsData,
        loading: snap.uiLayouts.loading,
        error: snap.uiLayouts.error,
    }), [uiLayoutsData, snap.uiLayouts.loading, snap.uiLayouts.error]);

    const requirementsMet = !snap.form.loading && !snap.formVersion.loading && !snap.formType.loading;
    const ready = requirementsMet
        && !snap.uiLayout.loading
        && !snap.formVersions.loading
        && !snap.formTypes.loading;
    const selectedFormVersionMatchesId = !!snap.versionId && snap.formVersion.data !== null;

    return {
        formId: snap.formId,
        versionId: snap.versionId,
        uiLayoutId: snap.uiLayoutId,

        form,
        formVersion,
        formType,
        uiLayout,

        formVersions,
        formTypes,
        uiLayouts,

        selectedSplitOption: snap.selectedSplitOption,
        previewMode: snap.previewMode,
        insideFormContext: snap.formId !== undefined,

        isArchivingForm: snap.isArchivingForm,
        isUnarchivingForm: snap.isUnarchivingForm,
        isArchiving: snap.isArchiving,
        isUnarchiving: snap.isUnarchiving,

        splitQuery: snap.splitQuery as SplitQuery | undefined,
        formVersionAccess,

        ready,
        requirementsMet,
        selectedFormVersionMatchesId,

        navigateToForm: formActions.navigateToForm,
        navigateToVersion: formActions.navigateToVersion,
        selectUILayout: formActions.selectUILayout,
        setVisibilityMode: formActions.setVisibilityMode,
        setSelectedSplitOption: formActions.setSelectedSplitOption,
        archiveForm: formActions.archiveForm,
        unarchiveForm: formActions.unarchiveForm,
        archiveFormVersion: formActions.archiveFormVersion,
        unarchiveFormVersion: formActions.unarchiveFormVersion,
    };
};
